/**
 * controller.ts —— 牌桌编排器（引擎 + AI + 赢率 + 存档 + 埋点的唯一接线处）
 *
 * 为什么放在 React 之外：
 *   1. AI 思考停顿、发牌动画、结算等待都是定时器驱动的时序逻辑，放在组件里会被
 *      StrictMode 的双调用与依赖数组问题反复咬到；
 *   2. 引擎是可变对象，UI 只需要「变了就重渲染」，用一个 version store + useSyncExternalStore
 *      即可，无需把引擎状态复制成不可变树。
 *
 * 严格遵守 CONTRACT.md：不改引擎语义，所有规则判定都问引擎（legalActionsFor / act）。
 */

import { Game, type DealScript, type GameConfig, type HandRecord, type Street } from '../core/game';
import type { Action, ActionEffect } from '../core/betting';
import { IllegalActionError } from '../core/betting';
import { bestFiveCards, describeHandValue, evaluateHoleAndBoard, categoryOf, CATEGORY_NAMES_CN } from '../core/evaluator';
import { rankOf, formatCardDisplay } from '../core/cards';
import { AiTable, type StyleKey } from '../core/ai';
import { STYLE_KEYS } from '../core/ai/styles';
import { createRngBundle, deriveSeedPair, randomSeed } from '../core/rng';
import { EquityService, type EquityNode } from './equityService';
import { DEBUG_SLOW_EQUITY_DEFAULT_MS, DEBUG_SLOW_EQUITY_URL_MS, DEBUG_TOOLS } from './env';
import { createWorkerChannel, type EquityChannel } from './equityClient';
import type { EquitySnapshot, EquitySnapshotMap } from './equityTypes';
import { DOC_VERSION, clearDoc, loadDoc, saveDoc, type PersistedDoc } from './persistence';
import { settingsStore, patchSettings, type Settings } from './settings';
import { StatsBook } from './stats';
import { scoreAction, type ScoreRecord } from './scoring';
import { assignNicknames } from './opponents';
import { createStore, type Store } from './store';
import { pushToast } from './toasts';
import { track } from './telemetry';
import { actionText, STREET_CN, handNetText } from './format';

export interface SetupParams {
  readonly opponents: number;
  readonly startingStack: number;
  readonly smallBlind: number;
  readonly bigBlind: number;
  readonly ante: number;
  readonly seed: number | null;
  readonly styleMode: 'random' | 'manual';
  readonly styles: readonly StyleKey[];
}

export interface Bubble {
  readonly text: string;
  readonly kind: ActionEffect['type'];
}

/** 防连点锁定时长（设计方案 5.5：提交后 600ms）。 */
export const SUBMIT_LOCK_MS = 600;

/**
 * R1（第 9 轮）：开启「自动继续」时，非摊牌手的默认停留时长（ms）。
 * 关键点：**不除以 speed** —— 倍速只加速 AI 行动动画，不缩短复盘阅读窗口。
 * 取 5000（≥5s）满足「读净额 + 池归属」的最低阅读预算；玩家可随时点「继续」提前推进。
 */
export const AUTO_CONTINUE_MS = 5000;

/**
 * R13-2（模式 A）：turbo 下**非摊牌手**结算的一次性短自动推进窗口（ms）。
 * 复用观战自动续手的短节奏思路，让「加速」诉求在非摊牌手上真正更快进入下一手；
 * 玩家仍可随时点「继续」提前。摊牌手一律 null（R1 红线，不受 turbo 影响）。
 * 这是本手一次性行为，**不修改** `settings.autoContinue`。
 */
export const TURBO_CONTINUE_MS = 1200;

/** 本手结算摘要（结算浮层）。R5：聚合赢家单一焦点 + 需高亮的制胜 5 张。 */
export interface HandSummary {
  readonly net: number;
  readonly text: string;
  readonly awards: string[];
  /** 是否摊牌手（有 showdown 事件）：摊牌手一律等待玩家主动继续（R1）。 */
  readonly isShowdown: boolean;
  /** R5：赢家单一焦点 —— 胜者名 + 主池金额 + 制胜牌型（非摊牌手无牌型）。 */
  readonly focus: { readonly winners: string; readonly amount: number; readonly handText: string | null } | null;
  /** R3/R5：需高亮的制胜 5 张牌（各主池摊牌赢家最优成手的并集）。 */
  readonly winningCards: readonly number[];
  /**
   * T8 · AC-N2：英雄本手的结构化陈述素材——底牌 + 成手牌型名（摊牌手才有）+ 是否弃牌。
   * 供结算首屏「你的牌 A♠K♦ · 两对 · +78」一行说人话，首屏不出现 EV/GTO/VPIP 术语。
   */
  readonly hero: {
    readonly holeText: string | null;
    readonly handText: string | null;
    readonly folded: boolean;
  } | null;
  /** T8 · AC-N1：净盈利 > 0 时供评分区首行「你赢下 +78 · 两对」结果陈述（负面措辞降为次级）。 */
  readonly won: boolean;
}

const HUMAN_SEAT = 0;

export class GameController {
  readonly version: Store<number> = createStore(0);
  readonly equity: EquityService;
  private readonly channel: EquityChannel;

  game: Game | null = null;
  private ai: AiTable | null = null;
  styles: Record<number, StyleKey> = {};
  stats = new StatsBook();
  snapshots: Record<number, Partial<Record<Street, EquitySnapshot>>> = {};
  scores: ScoreRecord[] = [];

  /** 席位动作气泡。 */
  bubbles = new Map<number, Bubble>();
  thinkingSeat: number | null = null;
  winners = new Set<number>();
  /** 发牌 / 翻牌动画进行中：行动区为禁用态（状态机未就绪）。 */
  busyUntil = 0;
  /** 本手结算摘要（结算浮层）。 */
  handSummary: HandSummary | null = null;
  nextHandAt: number | null = null;
  humanEliminated = false;
  observing = false;
  /**
   * R13-1：随时弃牌意图。非我方行动轮点「认输本手」时置位，UI 立即把「我」呈现为已退出本手；
   * 引擎不越轮写入，待 tick 推进到 `toAct===HUMAN` 时以合法 fold 落地并清除本标志。
   * 本手瞬时态：startHand / reset 必清空，禁止跨手泄漏。
   */
  pendingConcede = false;
  /**
   * R13-2：本手加速覆盖（turbo）。为 true 时把「本手剩余 AI 行动」的 thinkMs / 街揭示 / 结算间隔
   * 压到 ≈0，只影响节奏、不改 AI 决策与随机序、不写 settings.speed。本手瞬时态，startHand / reset 清空。
   */
  turbo = false;
  /**
   * R13-2：弃牌后二选一浮层是否展示。仅当我弃牌落地、本手仍需 ≥2 名 AI 继续、且「每次询问」开启时置位。
   * 本手瞬时态，startHand / reset / afterHandComplete 清空。
   */
  foldAdvancePrompt = false;
  /**
   * M-FF（F-2）：本手「弃牌后二选一」是否已决策（弹窗展开 or 记忆值落定）的判重标志。
   * 越轮弃牌下我们在点击时即用牌局快照同步决策（F-1），随后 tick 推进到我方轮次 fold 落地时
   * `onHumanFolded` 会再次进入决策点——本标志确保二选一浮层只弹一次、记忆值埋点只上报一次。
   * 本手瞬时态：startHand / reset / afterHandComplete 清空，禁止跨手泄漏。
   */
  foldAdvanceResolved = false;
  submitLockUntil = 0;
  lastActionError: string | null = null;
  clockRemainingMs: number | null = null;
  peakStack = 0;
  saveError: string | null = null;
  /**
   * 最近一次评分的标识（第 10 轮 R3：事件驱动，不再自动淡出）。
   *   - `at`：该条评分的唯一时间戳标识，用于 UI 层的显式关闭判定（`scoreTipVisible`）；
   *   - `handNumber`：所属手牌，供 UI 从 `scores` 里筛出「本手」评分做 k/n 翻页。
   * 展示内容（三段式解析、k/n）全部由 UI 从 `scores` 里对应记录用 `buildScoreDetail` 现算，
   * 控制器只负责「哪条是最新、属于哪一手」这两个事实。
   */
  lastScore: { at: number; handNumber: number } | null = null;

  private runToken = 0;
  private loopTimer: ReturnType<typeof setTimeout> | null = null;
  private clockTimer: ReturnType<typeof setInterval> | null = null;
  private lastStreetKey = '';

  constructor(channel?: EquityChannel) {
    this.channel = channel ?? createWorkerChannel();
    this.equity = new EquityService({
      channel: this.channel,
      onSnapshot: (hand, street, snap) => {
        const cur = this.snapshots[hand] ?? {};
        this.snapshots[hand] = { ...cur, [street]: snap };
        this.bump();
      },
      getSnapshot: (hand, street) => this.snapshots[hand]?.[street] ?? null,
      onToast: (t, kind) => pushToast(t, kind ?? 'info'),
      // 调试慢速（诊断包专用）：给 Worker 一个「总注入预算」，由 Worker 摊到真实进度检查点上。
      // 只拉长耗时，不伪造进度、不改变任何结果数值。
      // 两条入口，URL 优先（AC4.6：QA 需要一条无状态、单次 goto 即生效的确定性手段）：
      //   1. 诊断包 `?slowEquity=1200`（毫秒）；
      //   2. 设置面板「调试：拖慢赢率计算」开关 → 缺省 1200ms。
      // 生产包里 DEBUG_TOOLS 为 false：整段折叠成常量 0，存档里残留 debugSlowEquity=true 也不会生效。
      slowBurnMs: () => {
        if (!DEBUG_TOOLS) return 0;
        if (DEBUG_SLOW_EQUITY_URL_MS > 0) return DEBUG_SLOW_EQUITY_URL_MS;
        return settingsStore.get().debugSlowEquity ? DEBUG_SLOW_EQUITY_DEFAULT_MS : 0;
      },
    });

    // REG2-NEW-003：存档里 equityEnabled=true 时，把「开关态」同步进赢率服务。
    // 此前只有设置面板的 UI 开关是 true，而 EquityService 内部 enabled 仍是初始 false，
    // 于是刷新后出现「开关显示已开、面板 phase=off 不算」的分叉态。
    if (this.settings.equityEnabled) this.equity.restoreEnabled(true);
  }

  private get settings(): Settings {
    return settingsStore.get();
  }

  bump(): void {
    this.version.set((v) => v + 1);
  }

  /* ------------------------------------------------------------------ */
  /* 开局 / 续局                                                         */
  /* ------------------------------------------------------------------ */

  startNewGame(p: SetupParams): void {
    this.reset();
    const seatCount = p.opponents + 1;
    const userSeed = p.seed ?? randomSeed();
    const rng = createRngBundle(deriveSeedPair(userSeed));

    // 风格构成：手动指定时按顺序落座；随机时用 aiRNG 抽取（并显式记录，保证续局可复现）
    const aiSeats = Array.from({ length: seatCount - 1 }, (_, i) => i + 1);
    const styles: Record<number, StyleKey> = {};
    aiSeats.forEach((seat, i) => {
      styles[seat] =
        p.styleMode === 'manual' && p.styles[i]
          ? p.styles[i]
          : STYLE_KEYS[rng.aiRNG.nextInt(STYLE_KEYS.length)];
    });

    /*
     * R6（第 10 轮）：开局即把「风格昵称」写进 `GameConfig.names`。
     * 昵称是风格的纯函数（`assignNicknames`），因此同座位昵称与其风格一致、同局稳定（R6.5）；
     * 写进 config → 落到 `player.name` → 随引擎存档持久化，续局恢复后昵称不丢（引擎按 player.name 存读）。
     */
    const nickBySeat = assignNicknames(styles);
    const names = Array.from({ length: seatCount }, (_, seat) =>
      seat === HUMAN_SEAT ? '你' : nickBySeat[seat] ?? `电脑${seat}`,
    );

    const config: GameConfig = {
      seatCount,
      startingStack: p.startingStack,
      smallBlind: p.smallBlind,
      bigBlind: p.bigBlind,
      ante: p.ante,
      humanSeat: HUMAN_SEAT,
      seed: userSeed,
      names,
    };
    const game = new Game(config, rng);

    this.styles = styles;
    this.game = game;
    this.ai = new AiTable(aiSeats, styles, p.startingStack, rng.aiRNG);
    this.peakStack = p.startingStack;

    track('game_start', {
      players: seatCount,
      stack: p.startingStack,
      sb: p.smallBlind,
      bb: p.bigBlind,
      ante: p.ante,
      speed: this.settings.speed,
      styles: aiSeats.map((s) => styles[s]),
      custom_seed: p.seed !== null,
      seed: userSeed,
    });

    this.startHand();
  }

  /** 从存档恢复（AC1.9 / AC5.6）。返回是否成功。 */
  resumeFromDoc(doc: PersistedDoc): boolean {
    this.reset();
    try {
      const game = Game.load(doc.engine);
      this.game = game;
      this.styles = { ...doc.styles };
      const aiSeats = Object.keys(doc.styles).map(Number);
      // AI 情绪状态不在引擎存档契约内，恢复后从 calm 重新累积（见交付说明「已知问题」）
      this.ai = new AiTable(aiSeats, this.styles, game.config.startingStack, game.rng.aiRNG);
      this.snapshots = JSON.parse(JSON.stringify(doc.snapshots)) as Record<number, Partial<Record<Street, EquitySnapshot>>>;
      this.stats = new StatsBook();
      this.stats.ingestAll(game.handHistory());
      this.peakStack = doc.peakStack;
      track('save_resume', { result: 'success', hand: game.handNumber });

      const me = game.players[HUMAN_SEAT];
      this.humanEliminated = !!me?.eliminated;
      if (game.phase === 'gameOver') {
        this.bump();
        return true;
      }
      if (game.phase === 'idle' || game.phase === 'handComplete') {
        this.startHand();
        return true;
      }
      // 手牌进行中：重新起算当前节点并恢复循环
      this.syncEquityNode(true);
      this.busyUntil = this.nowMs() + 240 / this.settings.speed;
      this.schedule(240 / this.settings.speed);
      this.bump();
      return true;
    } catch (e) {
      track('save_resume', { result: 'failed', error: e instanceof Error ? e.message : String(e) });
      pushToast(e instanceof Error ? e.message : '存档恢复失败', 'error');
      this.game = null;
      this.bump();
      return false;
    }
  }

  reset(): void {
    this.runToken += 1;
    if (this.loopTimer) clearTimeout(this.loopTimer);
    if (this.clockTimer) clearInterval(this.clockTimer);
    this.loopTimer = null;
    this.clockTimer = null;
    this.equity.cancel('hand_ended');
    this.game = null;
    this.ai = null;
    this.stats = new StatsBook();
    this.snapshots = {};
    this.scores = [];
    this.bubbles = new Map();
    this.thinkingSeat = null;
    this.winners = new Set();
    this.handSummary = null;
    this.nextHandAt = null;
    this.humanEliminated = false;
    this.observing = false;
    this.pendingConcede = false;
    this.turbo = false;
    this.foldAdvancePrompt = false;
    this.foldAdvanceResolved = false;
    this.lastActionError = null;
    this.clockRemainingMs = null;
    this.lastScore = null;
    this.busyUntil = 0;
    this.lastStreetKey = '';
    this.bump();
  }

  /* ------------------------------------------------------------------ */
  /* 一手牌                                                              */
  /* ------------------------------------------------------------------ */

  startHand(script?: DealScript): void {
    const g = this.game;
    if (!g) return;
    if (g.phase === 'gameOver') return;
    if (g.fundedSeats().length < 2) return;

    this.bubbles = new Map();
    this.winners = new Set();
    this.handSummary = null;
    this.nextHandAt = null;
    this.thinkingSeat = null;
    this.lastActionError = null;
    this.lastScore = null;
    // R13：跨手清理本手瞬时态，杜绝 pendingConcede / turbo / 二选一浮层泄漏到新一手（AC1.8）。
    this.pendingConcede = false;
    this.turbo = false;
    this.foldAdvancePrompt = false;
    this.foldAdvanceResolved = false;
    // R13：上一手末动作的防连点提交锁不得跨手阻塞新一手首个动作（否则快速续手时我方 fold/认输会被静默拒绝）。
    this.submitLockUntil = 0;
    this.clockRemainingMs = null;
    try {
      g.startHand(script);
    } catch (e) {
      pushToast(e instanceof Error ? e.message : '开始新一手失败', 'error');
      return;
    }
    this.lastStreetKey = this.streetKey();
    this.syncEquityNode(true);

    // 发牌动画：逐张 120ms + 60ms 间隔（1x），期间行动区为禁用态
    const n = g.activeSeats().length;
    const dealMs = (n * 2 * 60 + 240) / this.settings.speed;
    this.busyUntil = this.nowMs() + (this.motionOff() ? 80 : dealMs);
    this.startClockIfNeeded();
    this.schedule(this.busyUntil - this.nowMs());
    this.bump();
  }

  private streetKey(): string {
    const g = this.game;
    if (!g) return '';
    return `${g.handNumber}:${g.street}:${g.board.length}`;
  }

  /** 把当前节点推给赢率服务。cancelFirst 用于「发出新公共牌」这一取消触发点。 */
  private syncEquityNode(newNode: boolean): void {
    const g = this.game;
    if (!g) return;
    const me = g.players[HUMAN_SEAT];
    if (!me || !me.hole || !me.inHand || me.folded) return;
    if (g.phase === 'handComplete' || g.phase === 'gameOver' || g.phase === 'idle') return;
    const node: EquityNode = {
      handNumber: g.handNumber,
      street: g.street,
      hole: me.hole,
      board: [...g.board],
      activeOpponents: Math.max(0, g.activeSeats().length - 1),
    };
    if (newNode) this.equity.startNode(node);
  }

  currentNode(): EquityNode | null {
    const g = this.game;
    if (!g) return null;
    const me = g.players[HUMAN_SEAT];
    if (!me || !me.hole) return null;
    return {
      handNumber: g.handNumber,
      street: g.street,
      hole: me.hole,
      board: [...g.board],
      activeOpponents: Math.max(0, g.activeSeats().length - 1),
    };
  }

  /**
   * FR-5：英雄牌型提示（纯展示层，不改 evaluator / 结算 / EV / 赢率）。
   * 翻牌前（board.length===0）内核只接受 5~7 张，故走 2 张手牌兜底：口袋对=一对，否则高牌；
   * board ≥3 张时复用现有内核 `categoryOf(evaluateHoleAndBoard(hole, board))` → `CATEGORY_NAMES_CN`。
   * 仅在英雄在手且未弃牌时返回文案，随公共牌 0→3→4→5 自然刷新（读的是当前 board）。
   */
  heroCategory(): string | null {
    const g = this.game;
    if (!g) return null;
    const me = g.players[HUMAN_SEAT];
    if (!me || !me.hole || !me.inHand || me.folded) return null;
    const board = g.board;
    if (board.length === 0) {
      return rankOf(me.hole[0]) === rankOf(me.hole[1]) ? '一对' : '高牌';
    }
    return CATEGORY_NAMES_CN[categoryOf(evaluateHoleAndBoard(me.hole, board))];
  }

  /* ------------------------------------------------------------------ */
  /* 主循环：AI 行动与街推进                                              */
  /* ------------------------------------------------------------------ */

  private nowMs(): number {
    return typeof performance !== 'undefined' ? performance.now() : Date.now();
  }

  private motionOff(): boolean {
    const s = this.settings;
    if (s.motion === 'off') return true;
    if (s.motion === 'on') return false;
    return typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  }

  private schedule(delay: number): void {
    if (this.loopTimer) clearTimeout(this.loopTimer);
    const token = this.runToken;
    this.loopTimer = setTimeout(() => {
      if (token !== this.runToken) return;
      this.tick();
    }, Math.max(0, delay));
  }

  /** 主循环单步。 */
  private tick(): void {
    const g = this.game;
    if (!g) return;
    const wait = this.busyUntil - this.nowMs();
    if (wait > 4) {
      this.schedule(wait);
      return;
    }
    if (g.phase === 'handComplete' || g.phase === 'gameOver') {
      this.afterHandComplete();
      return;
    }
    const seat = g.toAct;
    if (seat === null) return;
    if (seat === HUMAN_SEAT && !this.humanEliminated) {
      this.thinkingSeat = null;
      /*
       * R13-1：越轮弃牌意图落地点。轮到我时若挂着 pendingConcede，则以合法 fold 经 applyAction 提交
       * （复用超时兜底同源路径，source='pending-concede'），随后清标志。这样引擎收到 fold 的时刻
       * `seat===toAct` 恒成立（AC1.3），越轮弃牌与轮内弃牌走同一条 fold 路径 → 结算逐字段等价（AC1.2）。
       */
      if (this.pendingConcede) {
        this.pendingConcede = false;
        this.clockRemainingMs = null;
        this.equity.onPlayerActed();
        const ok = this.applyAction(HUMAN_SEAT, { type: 'fold' }, true, 'pending-concede');
        if (ok) this.onHumanFolded();
        return;
      }
      /*
       * R3（第 10 轮）：轮到玩家重新行动 = 进入「下一步行动阶段」，关闭上一条评分（AC3.3）。
       * 这只在玩家面对**新的一个决策**时触发（自己刚行动后不会立刻又轮到自己），
       * 因此不会把刚展示的评分误关；弃牌后本手结束、AI 连续行动期间画面变化都不触发关闭
       * （lastScore 保持可见，直到本手结束进下一手由 startHand 清空）——正是 #3 要解决的「一闪而过」。
       */
      if (this.lastScore !== null) this.lastScore = null;
      this.startClockIfNeeded();
      this.bump();
      return;
    }
    this.driveAi(seat);
  }

  private driveAi(seat: number): void {
    const g = this.game;
    const ai = this.ai;
    if (!g || !ai || !ai.has(seat)) return;
    let decision;
    try {
      decision = ai.act(g, seat, g.rng.aiRNG, this.settings.speed);
    } catch (e) {
      pushToast(`AI 决策异常：${e instanceof Error ? e.message : String(e)}`, 'error');
      return;
    }
    this.thinkingSeat = seat;
    this.bump();
    // R13-2：turbo 只压缩本手剩余 AI 行动的**思考时延**（→0），不重新调用 ai.act、不改决策与随机序。
    const think = this.turbo ? 0 : this.motionOff() ? 120 : Math.max(120, decision.log.thinkMs);
    const token = this.runToken;
    this.loopTimer = setTimeout(() => {
      if (token !== this.runToken) return;
      this.applyAction(seat, decision.action, false);
    }, think);
  }

  /** 玩家提交动作。返回是否被引擎接受。 */
  humanAct(action: Action): boolean {
    const g = this.game;
    if (!g) return false;
    if (g.toAct !== HUMAN_SEAT) return false;
    if (this.nowMs() < this.submitLockUntil) return false; // 防连点
    if (this.busyUntil > this.nowMs()) return false;

    // 评分要在动作生效前取到「本街节点」的精确赢率快照（行动前不展示任何建议）
    const snap = this.snapshots[g.handNumber]?.[g.street] ?? null;
    const me = g.players[HUMAN_SEAT];
    const legal = g.legalActionsFor(HUMAN_SEAT);
    const potBefore = g.pot;
    const myStreetBefore = me?.streetContributed ?? 0;

    // 取消触发点之一：玩家已行动（AC4.7）
    this.equity.onPlayerActed();

    /*
     * R3（第 10 轮）：玩家提交下一个动作即关闭上一条评分（事件驱动的关闭点之一）。
     * 先清空旧评分，再在本动作被评分时写入新评分——即便本动作无评分（scoreAction 返回 null
     * 或评分关闭），旧评分也不残留（AC3.1(a) / AC3.2）。
     */
    this.lastScore = null;

    const ok = this.applyAction(HUMAN_SEAT, action, true);
    if (ok && action.type === 'fold') {
      // R13-2：轮内弃牌与越轮弃牌共用同一后继决策——弃牌落地后（本手仍需 AI 继续时）弹二选一 / 按记忆推进。
      this.onHumanFolded();
    }
    if (ok && snap && me && this.settings.scoreEnabled) {
      const res = scoreAction({
        equity: snap.equity,
        pot: potBefore,
        legal,
        action,
        streetContributed: myStreetBefore,
      });
      if (res) {
        this.scores.push({
          handNumber: g.handNumber,
          street: STREET_CN[snap.street],
          actionText: actionTextFor(action),
          tier: res.tier,
          evDiffRatio: res.evDiffRatio,
          // R4：三段式解析所需的原始字段，全部取自 scoreAction 输出，随手牌历史持久化
          equity: res.equity,
          pot: res.pot,
          callAmount: res.callAmount,
          bestLabel: res.bestLabel,
          // 需求二 FR2-1：随记录保存本桌总席位数，供详解模板「多人局面 → 边际 / 复杂」保守兜底判定；复盘可复现。
          seatCount: g.config.seatCount,
        });
        this.lastScore = { at: Date.now(), handNumber: g.handNumber };
        track('score_shown', { hand: g.handNumber, tier: res.tier, ev_diff: res.evDiffRatio });
      }
    }
    return ok;
  }

  /* ------------------------------------------------------------------ */
  /* R13-1 随时弃牌 / R13-2 弃牌后二选一推进                              */
  /* ------------------------------------------------------------------ */

  /**
   * R13-1：常驻「认输本手」入口的落点（已过二次确认）。
   *   - 轮到我：等价于当前弃牌，走 humanAct({fold})（引擎立即接受）。
   *   - 非我轮：置 pendingConcede 意图，UI 立即呈现我已退出本手；tick 推进到我方轮次时自动落地。
   * 幂等：已弃牌 / 已置意图 / 观战 / 非进行中 / 发牌动画中一律无副作用。
   */
  concedeAnytime(): void {
    const g = this.game;
    if (!g || this.humanEliminated) return;
    if (g.phase !== 'preflop' && g.phase !== 'flop' && g.phase !== 'turn' && g.phase !== 'river') return;
    const me = g.players[HUMAN_SEAT];
    if (!me || !me.inHand || me.folded) return; // 已弃牌 / 不在手内：不可再认输（幂等）
    if (this.pendingConcede) return; // 意图已置位：幂等，不重复
    const outOfTurn = g.toAct !== HUMAN_SEAT;
    track('fold_anytime', { out_of_turn: outOfTurn, street: g.street });
    if (!outOfTurn) {
      // 轮到我：与行动栏弃牌同一条路径（含评分 / 埋点 / 后继二选一）。
      // 发牌 / 提交锁等瞬时态由 humanAct 自行判定（此刻拒绝则无副作用，用户可稍后再点）。
      this.humanAct({ type: 'fold' });
      return;
    }
    // 非我轮：只置意图，绝不越轮调用 g.act（会抛 NOT_YOUR_TURN）。
    // 意图是「越轮预约」，即便此刻正在发牌动画也应记录——它会在推进到我方合法轮次时才落地。
    this.pendingConcede = true;
    this.clockRemainingMs = null;
    // UI 即时反馈：我方席位气泡显示「弃牌」、行动区收起（GameScreen 依 pendingConcede 呈现退出态）。
    this.bubbles.set(HUMAN_SEAT, { text: '弃牌', kind: 'fold' });
    this.bump();
    // M-FF（F-1）：点击即置态——用当前牌局快照同步评估后继二选一，把「按钮显示」与「引擎 fold 落地」解耦。
    // 我方 fold 尚未落地（activeSeats 此刻仍含 HUMAN_SEAT），故仍需行动的席位数须扣除自己。
    // 这样「快进/原速」按钮在点击的同步栈内即可显示（≤1 帧），不再随中间夹的 AI 数量抖动而偶发延迟。
    this.resolveFoldAdvance(g.activeSeats().length - 1);
  }

  /**
   * Round21 T6 / AC-S1：取消尚未落地的越轮弃牌预选（pendingConcede 意图）。
   *
   * 预选是「越轮预约」，只是一个意图标志、尚未在引擎侧 fold 落地（落地发生在推进到我方合法轮次时，
   * 见 tick 中的 `pending-concede` 分支）。因此在**未落地**期间清除它是完全安全、可逆的：不触碰引擎
   * 状态、不产生任何 fold。幂等：未置意图 / 已弃牌 / 已出局 / 非进行中一律无副作用。
   */
  cancelConcede(): void {
    const g = this.game;
    if (!g || !this.pendingConcede) return;
    const me = g.players[HUMAN_SEAT];
    if (!me || me.folded) return; // 已落地为 fold：不可撤销
    this.pendingConcede = false;
    // 撤销 UI 即时反馈：清掉「弃牌」气泡，恢复行动区。
    if (this.bubbles.get(HUMAN_SEAT)?.kind === 'fold') this.bubbles.delete(HUMAN_SEAT);
    // 预选时已同步评估过弃牌后二选一（resolveFoldAdvance）；取消后一并回滚，避免残留 prompt/turbo，
    // 使后续真正弃牌能重新评估。
    this.foldAdvancePrompt = false;
    this.foldAdvanceResolved = false;
    this.turbo = false;
    track('fold_anytime_cancel', { street: g.street });
    this.bump();
  }

  /**
   * R13-2：二选一浮层的选择落点（turbo=加速本手 / normal=原速）。
   * remember=true 时写入 settings（记忆值 + 关闭「每次询问」），设置面板可改回。
   */
  chooseFoldAdvance(mode: 'turbo' | 'normal', remember: boolean): void {
    this.foldAdvancePrompt = false;
    if (remember) patchSettings({ foldAdvanceMode: mode, foldAdvanceAsk: false });
    track('fold_advance_mode', { mode, from_memory: false, remembered: remember });
    if (mode === 'turbo') this.turbo = true;
    this.bump();
  }

  /**
   * R13-2 / M-FF：弃牌后二选一浮层的统一决策点。
   * 由两处调用：
   *   - 路径 A（轮内弃牌）与越轮弃牌的引擎落地：`onHumanFolded()`，此时 fold 已生效，`activeSeats()` 已不含我；
   *   - 路径 B（越轮弃牌）的点击瞬间：`concedeAnytime()` 越轮分支，用当前牌局快照同步决策（F-1），
   *     此刻我方 fold 尚未落地，故传入的 remainingActive 需扣除我自己。
   * `foldAdvanceResolved` 判重（F-2）：无论「弹窗」还是「记忆值落定」，本手只决策一次，
   * 杜绝落地时二次弹窗 / 重复上报 fold_advance_mode。
   * @param remainingActive 我方 fold 落地后本手仍在手内、需继续行动的席位数
   */
  private resolveFoldAdvance(remainingActive: number): void {
    const g = this.game;
    if (!g) return;
    if (this.foldAdvanceResolved) return; // F-2 判重：本手已决策，绝不二次弹窗 / 重复埋点
    if (g.phase === 'handComplete' || g.phase === 'gameOver') return; // 本手已结束：走 R1，不弹
    if (remainingActive < 2) return; // 无需 ≥2 名 AI 再行动：不弹
    this.foldAdvanceResolved = true;
    if (this.settings.foldAdvanceAsk) {
      this.foldAdvancePrompt = true;
      this.bump();
      return;
    }
    const mode = this.settings.foldAdvanceMode;
    track('fold_advance_mode', { mode, from_memory: true, remembered: true });
    if (mode === 'turbo') this.turbo = true; // F-3：记忆值 turbo 在意图期即生效，driveAi 天然读取
    this.bump();
  }

  /**
   * R13-2：我弃牌落地后的后继处理。fold 已生效（`activeSeats()` 不含我），交由统一决策点判定。
   * 越轮弃牌下点击时已同步决策（见 concedeAnytime），此处经 foldAdvanceResolved 判重跳过，行为与轮内弃牌等价（F-2）。
   */
  private onHumanFolded(): void {
    const g = this.game;
    if (!g) return;
    this.resolveFoldAdvance(g.activeSeats().length);
  }

  private applyAction(
    seat: number,
    action: Action,
    isHuman: boolean,
    source: 'manual' | 'timeout' | 'pending-concede' = 'manual',
  ): boolean {
    const g = this.game;
    if (!g) return false;
    const streetBefore = this.streetKey();
    const potBefore = g.pot;
    let effect: ActionEffect;
    try {
      effect = g.act(seat, action);
    } catch (e) {
      if (e instanceof IllegalActionError) {
        this.lastActionError = e.message;
        if (!isHuman) pushToast(`AI 动作被规则引擎拒绝：${e.message}`, 'error');
        this.bump();
        return false;
      }
      pushToast(e instanceof Error ? e.message : String(e), 'error');
      this.bump();
      return false;
    }

    this.lastActionError = null;
    this.thinkingSeat = null;
    this.bubbles.set(seat, { text: actionText(effect), kind: effect.type });
    if (isHuman) {
      this.submitLockUntil = this.nowMs() + SUBMIT_LOCK_MS;
      this.clockRemainingMs = null;
    }
    track('player_action', {
      seat,
      is_human: isHuman,
      street: g.street,
      action: effect.type,
      raw: effect.rawType,
      amount: effect.chips,
      pot: potBefore,
      stack_left: g.players[seat]?.stack ?? 0,
      // 与超时兜底路径区分：'manual' = 玩家/AI 主动提交，'timeout' = 时限归零后的托管动作，
      // 'pending-concede' = R13-1 越轮弃牌意图在我方合法轮次落地。
      source,
    });

    // 街推进 / 本手结束的后处理
    const streetAfter = this.streetKey();
    if (g.phase === 'handComplete' || g.phase === 'gameOver') {
      this.equity.onHandEnded();
      this.afterHandComplete();
      return true;
    }
    if (streetAfter !== streetBefore) {
      this.lastStreetKey = streetAfter;
      // 取消触发点之一：发出新公共牌
      this.equity.cancel('new_community_card');
      this.syncEquityNode(true);
      // R13-2：turbo 把街揭示间隔压到 0（只影响节奏，公共牌/结算数值不变）。
      const reveal = this.turbo ? 0 : this.motionOff() ? 80 : (g.street === 'flop' ? 360 : 300) / this.settings.speed;
      this.busyUntil = this.nowMs() + reveal;
    }
    this.bump();
    this.schedule(Math.max(0, this.busyUntil - this.nowMs()) + (this.turbo ? 0 : 60));
    return true;
  }

  /* ------------------------------------------------------------------ */
  /* 结算 / 淘汰 / 终局                                                   */
  /* ------------------------------------------------------------------ */

  private afterHandComplete(): void {
    const g = this.game;
    if (!g) return;
    if (this.handSummary) return; // 已处理
    const history = g.handHistory();
    const rec = history[history.length - 1];
    if (!rec) return;

    this.stats.ingest(rec);
    const st = rec.settlement;
    const winners = new Set<number>();
    const awards: string[] = [];
    if (st) {
      st.awards.forEach((a) => {
        a.winners.forEach((w) => winners.add(w));
        const name = a.winners.map((w) => g.players[w]?.name ?? `座位 ${w}`).join('、');
        const label = a.potIndex === 0 ? '主池' : `边池 ${a.potIndex}`;
        const extra = a.remainderSeats.length
          ? `（含零头 +1：${a.remainderSeats.map((s) => g.players[s]?.name ?? s).join('、')}）`
          : '';
        awards.push(`${label} ${a.amount} → ${name}${extra}`);
      });
    }
    this.winners = winners;
    const myNet = rec.net.get(HUMAN_SEAT) ?? 0;

    // R5：赢家单一焦点 —— 主池（potIndex 0）的胜者 + 金额 + 制胜牌型。
    // 摊牌手才有牌型（describeHandValue）；全员弃牌的非摊牌手只给「胜者 + 金额」。
    const isShowdownHand = rec.events.some((e) => e.kind === 'showdown');
    const showdown = rec.events.find((e) => e.kind === 'showdown') as
      | { kind: 'showdown'; reveals: readonly { seat: number; hole: readonly [number, number]; value: number }[] }
      | undefined;
    const mainAward = st?.awards.find((a) => a.potIndex === 0) ?? st?.awards[0] ?? null;
    let focus: HandSummary['focus'] = null;
    const winningCards = new Set<number>();
    if (mainAward) {
      const focusWinnerNames = mainAward.winners.map((w) => g.players[w]?.name ?? `座位 ${w}`).join('、');
      let handText: string | null = null;
      if (showdown) {
        // 主池赢家的最优成手：既给焦点牌型文案，也给需高亮的 5 张（R3/R5）。
        for (const w of mainAward.winners) {
          const rev = showdown.reveals.find((r) => r.seat === w);
          if (!rev) continue;
          if (handText === null) handText = describeHandValue(rev.value).categoryName;
          for (const card of bestFiveCards([...rev.hole, ...rec.board])) winningCards.add(card);
        }
      }
      focus = { winners: focusWinnerNames, amount: mainAward.amount, handText };
    }

    // T8 · AC-N2：英雄结构化陈述素材。底牌恒可读（含弃牌手，用引擎保留的 hole）；
    // 成手牌型名仅摊牌手可得（英雄参与摊牌才有 reveal）。
    const heroPlayer = g.players[HUMAN_SEAT];
    const heroFolded = !!heroPlayer?.folded;
    const heroHoleText = heroPlayer?.hole ? heroPlayer.hole.map(formatCardDisplay).join(' ') : null;
    let heroHandText: string | null = null;
    if (showdown) {
      const heroRev = showdown.reveals.find((r) => r.seat === HUMAN_SEAT);
      if (heroRev) heroHandText = describeHandValue(heroRev.value).categoryName;
    }
    // T8 · AC-N1：净盈利 > 0 即「赢下本手」，供评分区首行先陈述结果。
    const won = myNet > 0;

    this.handSummary = {
      net: myNet,
      text: handNetText(myNet),
      awards,
      isShowdown: isShowdownHand,
      focus,
      winningCards: [...winningCards],
      hero: { holeText: heroHoleText, handText: heroHandText, folded: heroFolded },
      won,
    };

    const me = g.players[HUMAN_SEAT];
    if (me) this.peakStack = Math.max(this.peakStack, me.stack);
    const hadSidePot = (st?.pots.length ?? 0) > 1;
    track('hand_end', {
      hand: rec.handNumber,
      players: rec.events.filter((e) => e.kind === 'deal').length,
      side_pot: hadSidePot,
      total: st?.totalPaid ?? 0,
      winners: [...winners],
      rit: false,
    });

    // AI 情绪更新（PRD 2.1 ②）
    this.ai?.endHand(rec.net, g.rng.aiRNG);

    if (me?.eliminated && !this.humanEliminated) {
      this.humanEliminated = true;
      this.equity.forceOff();
      patchSettings({ equityEnabled: false });
    }
    const justEliminated = rec.events.filter((e) => e.kind === 'eliminated');
    if (justEliminated.length) {
      const names = justEliminated
        .map((e) => (e.kind === 'eliminated' ? g.players[e.seat]?.name ?? `座位 ${e.seat}` : ''))
        .join('、');
      pushToast(`${names} 已被淘汰`, 'warning');
    }

    this.persist();

    if (g.phase === 'gameOver') {
      this.bump();
      return;
    }

    /*
     * R1（第 9 轮）：结算推进策略。
     *
     *   观战态（玩家已出局、被动观看到底）：沿用与动画同步的较短节奏，由控制器自动续手，
     *     无需复盘阅读窗口，也无需 UI 淡出。
     *   在座玩家：**默认手动推进** —— 结算浮层停留，由 UI 层（GameScreen）在
     *     「点继续 / Enter/空格 / 自动继续到点」时带淡出推进（见 nextHandNow）。
     *     仅「开启自动继续 且 非摊牌手」才设一个 ≥5s 的自动截止点（AUTO_CONTINUE_MS，不除以 speed）；
     *     摊牌手一律 nextHandAt=null（纯手动），确保有时间读净额 + 主/边池归属 + 亮牌。
     */
    const observing = this.humanEliminated && this.observing;
    if (observing) {
      const wait = this.motionOff() ? 600 : Math.round(1400 / this.settings.speed);
      this.nextHandAt = Date.now() + wait;
      const token = this.runToken;
      this.loopTimer = setTimeout(() => {
        if (token !== this.runToken) return;
        if (this.humanEliminated && !this.observing) return; // 等玩家选择观战 / 回主页
        this.startHand();
      }, wait);
      this.bump();
      return;
    }
    this.nextHandAt =
      this.settings.autoContinue && !isShowdownHand ? Date.now() + AUTO_CONTINUE_MS : null;
    /*
     * R24（手动门控）：单机 handComplete 时 UI 恒 revealAll 全员亮牌，非摊牌手同样需要阅读时间。
     *   因此 turbo 不再对手结束的结算态施加短自动推进窗口（旧：TURBO_CONTINUE_MS / motionOff 400ms），
     *   而是与摊牌手同一红线：nextHandAt 仅由 settings.autoContinue（默认关=手动）决定。
     *   turbo 的加速语义收敛为「只压缩本手内 AI 行动 / 街揭示 / 结算间隔节奏」（见 startHand / stepAI），
     *   不再越过手结束后的结算查看；由结算 sticky footer 主 CTA（继续 / 下一手 / Enter / 空格）承接推进。
     *   摊牌手（nextHandAt=null 纯手动）、观战态、联机路径均不受影响。
     */
    // 弃牌后二选一浮层是本手瞬时态：进入结算即收起，避免残留到下一手（AC1.8）。
    this.foldAdvancePrompt = false;
    this.foldAdvanceResolved = false;
    this.bump();
  }

  /** 立即进入下一手（结算浮层按钮）。 */
  nextHandNow(): void {
    if (!this.game) return;
    if (this.game.phase !== 'handComplete') return;
    if (this.loopTimer) clearTimeout(this.loopTimer);
    this.startHand();
  }

  observeToEnd(): void {
    this.observing = true;
    this.bump();
    if (this.game?.phase === 'handComplete') this.startHand();
  }

  /* ------------------------------------------------------------------ */
  /* 行动时限（PRD 模块 3，默认关闭）                                      */
  /* ------------------------------------------------------------------ */

  private startClockIfNeeded(): void {
    const clock = this.settings.actionClock;
    if (this.clockTimer) clearInterval(this.clockTimer);
    this.clockTimer = null;
    if (!clock || !this.game || this.game.toAct !== HUMAN_SEAT || this.humanEliminated) {
      this.clockRemainingMs = null;
      return;
    }
    this.clockRemainingMs = clock * 1000;
    const token = this.runToken;
    this.clockTimer = setInterval(() => {
      if (token !== this.runToken) return;
      const g = this.game;
      if (!g || g.toAct !== HUMAN_SEAT) {
        if (this.clockTimer) clearInterval(this.clockTimer);
        this.clockTimer = null;
        this.clockRemainingMs = null;
        this.bump();
        return;
      }
      // 赢率计算期间倒计时暂停（PRD 4.6）
      const ph = this.equity.store.get().phase;
      if (ph === 'silent' || ph === 'loading' || ph === 'slow') {
        this.bump();
        return;
      }
      this.clockRemainingMs = Math.max(0, (this.clockRemainingMs ?? 0) - 250);
      if (this.clockRemainingMs === 0) {
        if (this.clockTimer) clearInterval(this.clockTimer);
        this.clockTimer = null;
        try {
          this.equity.onPlayerActed();
          const potBefore = g.pot;
          const eff = g.actTimeout(HUMAN_SEAT);
          this.bubbles.set(HUMAN_SEAT, { text: actionText(eff), kind: eff.type });
          /*
           * DEF-003 / QA-BUG-009：超时兜底是一次**真实发生的玩家动作**，必须上报。
           * 这条路径不经过 applyAction（引擎侧走的是 actTimeout 托管），改前因此完全没有
           * player_action 埋点，下游漏斗会把「超时弃牌」误算成「未行动」。
           * 字段与手动提交严格同源，只用 source 区分来源。
           */
          track('player_action', {
            seat: HUMAN_SEAT,
            is_human: true,
            street: g.street,
            action: eff.type,
            raw: eff.rawType,
            amount: eff.chips,
            pot: potBefore,
            stack_left: g.players[HUMAN_SEAT]?.stack ?? 0,
            source: 'timeout',
          });
          pushToast(`行动超时，已自动${eff.type === 'check' ? '过牌' : '弃牌'}`, 'warning');
          this.postTimeout();
        } catch {
          /* 引擎会拒绝非法超时动作，忽略 */
        }
      }
      this.bump();
    }, 250);
  }

  private postTimeout(): void {
    const g = this.game;
    if (!g) return;
    if (g.phase === 'handComplete' || g.phase === 'gameOver') {
      this.equity.onHandEnded();
      this.afterHandComplete();
      return;
    }
    const key = this.streetKey();
    if (key !== this.lastStreetKey) {
      this.lastStreetKey = key;
      this.equity.cancel('new_community_card');
      this.syncEquityNode(true);
    }
    this.schedule(200);
  }

  /* ------------------------------------------------------------------ */
  /* 赢率面板开关（局中设置面板）                                          */
  /* ------------------------------------------------------------------ */

  setEquityEnabled(on: boolean): void {
    if (on && this.humanEliminated) {
      pushToast('你已被淘汰，赢率口径依赖你的底牌，观战时不可用', 'warning');
      return;
    }
    patchSettings({ equityEnabled: on });
    this.equity.setEnabled(on, on ? this.currentNode() : null);
    this.bump();
  }

  setEquityMode(mode: 'live' | 'review'): void {
    patchSettings({ equityMode: mode });
    this.equity.setMode(mode, this.currentNode());
    this.bump();
  }

  setSpeed(speed: 1 | 2 | 4): void {
    patchSettings({ speed });
    track('speed_change', { target: speed });
    this.bump();
  }

  /* ------------------------------------------------------------------ */
  /* 存档                                                                */
  /* ------------------------------------------------------------------ */

  persist(): void {
    const g = this.game;
    if (!g) return;
    const doc: PersistedDoc = {
      docVersion: DOC_VERSION,
      savedAt: Date.now(),
      engine: g.save(),
      styles: this.styles,
      snapshots: this.snapshots as EquitySnapshotMap,
      peakStack: this.peakStack,
    };
    const r = saveDoc(doc);
    if (!r.ok) {
      this.saveError = r.error ?? '存档写入失败';
      pushToast('本次进度未能保存，可重试', 'warning');
    } else {
      this.saveError = null;
    }
  }

  exitAndSave(): void {
    this.persist();
    this.reset();
  }

  abandonSave(): void {
    clearDoc();
    this.bump();
  }

  loadSave(): ReturnType<typeof loadDoc> {
    return loadDoc();
  }

  handHistory(): readonly HandRecord[] {
    return this.game?.handHistory() ?? [];
  }

  dispose(): void {
    this.runToken += 1;
    if (this.loopTimer) clearTimeout(this.loopTimer);
    if (this.clockTimer) clearInterval(this.clockTimer);
    this.equity.dispose();
  }
}

function actionTextFor(a: Action): string {
  switch (a.type) {
    case 'fold':
      return '弃牌';
    case 'check':
      return '过牌';
    case 'call':
      return '跟注';
    case 'bet':
      return `下注 ${a.amount ?? ''}`;
    case 'raise':
      return `加注至 ${a.amount ?? ''}`;
    case 'allin':
      return '全下';
    default:
      return '';
  }
}
